'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import ResourceCard from './ResourceCard';
import VideoTutorialCard from './VideoTutorialCard';
import TimelineStep from './TimelineStep';
import FAQItem from './FAQItem';
import NewsletterSignup from './NewsletterSignup';

interface Resource {
  id: number;
  title: string;
  description: string;
  category: string;
  downloadUrl: string;
  image: string;
  alt: string;
  fileType: string;
  fileSize: string;
  downloads: number;
  isNew?: boolean;
}

interface VideoTutorial {
  id: number;
  title: string;
  description: string;
  thumbnail: string;
  alt: string;
  duration: string;
  views: number;
  category: string;
}

interface TimelineData {
  week: string;
  title: string;
  tasks: string[];
  isActive?: boolean;
}

interface FAQ {
  id: number;
  question: string;
  answer: string;
}

const ResourcesInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories = ['All', 'Moving Guides', 'Packing Tips', 'Checklists', 'Kuwait Specific'];

  const resources: Resource[] = [
  {
    id: 1,
    title: 'Complete Moving Checklist',
    description:
    'Comprehensive 8-week moving checklist covering every step from planning to settling in your new home',
    category: 'Checklists',
    downloadUrl: '#',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11fc8adac-1767714748371.png",
    alt: 'Person checking items on moving checklist with pen on clipboard',
    fileType: 'PDF',
    fileSize: '2.4 MB',
    downloads: 1247,
    isNew: true
  },
  {
    id: 2,
    title: 'Kuwait Summer Moving Guide',
    description:
    'Essential tips for moving during Kuwait\'s hot summer months, including heat protection and timing strategies',
    category: 'Kuwait Specific',
    downloadUrl: '#',
    image: "https://images.unsplash.com/photo-1706464971169-916a01782125",
    alt: 'Modern Kuwait cityscape with tall buildings under bright summer sun',
    fileType: 'PDF',
    fileSize: '1.8 MB',
    downloads: 892,
    isNew: true
  },
  {
    id: 3,
    title: 'Professional Packing Guide',
    description:
    'Room-by-room packing instructions with material recommendations and fragile item handling techniques',
    category: 'Packing Tips',
    downloadUrl: '#',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_103ef7112-1767343467678.png",
    alt: 'Hands carefully wrapping delicate glassware in bubble wrap for moving',
    fileType: 'PDF',
    fileSize: '3.1 MB',
    downloads: 2156
  },
  {
    id: 4,
    title: 'Farwaniya Area Moving Guide',
    description:
    'Neighborhood-specific moving information including parking regulations, building access, and local requirements',
    category: 'Kuwait Specific',
    downloadUrl: '#',
    image: "https://images.unsplash.com/photo-1580992847611-3beda0f38d16",
    alt: 'Residential street in Farwaniya Kuwait with apartment buildings and parked cars',
    fileType: 'PDF',
    fileSize: '1.5 MB',
    downloads: 634
  },
  {
    id: 5,
    title: 'Home Inventory Template',
    description:
    'Detailed inventory spreadsheet for cataloging belongings, tracking boxes, and managing insurance documentation',
    category: 'Checklists',
    downloadUrl: '#',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a2ded9a5-1766832859408.png",
    alt: 'Spreadsheet template on laptop screen showing organized inventory columns',
    fileType: 'XLSX',
    fileSize: '0.8 MB',
    downloads: 1523
  },
  {
    id: 6,
    title: 'First Week Essentials Box Guide',
    description:
    'Strategic packing list for essential items needed immediately after moving, organized by priority',
    category: 'Packing Tips',
    downloadUrl: '#',
    image: "https://images.unsplash.com/photo-1728034261578-afe827e282a2",
    alt: 'Open cardboard box labeled essentials with toiletries and basic items visible',
    fileType: 'PDF',
    fileSize: '1.2 MB',
    downloads: 1789
  }];


  const videoTutorials: VideoTutorial[] = [
  {
    id: 1,
    title: 'How to Pack Fragile Items',
    description:
    'Professional techniques for packing glassware, electronics, and delicate decorations safely',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1dbfad4f1-1765128184138.png",
    alt: 'Professional mover demonstrating bubble wrap technique on fragile vase',
    duration: '8:45',
    views: 3421,
    category: 'Packing Tutorial'
  },
  {
    id: 2,
    title: 'Furniture Disassembly Tips',
    description:
    'Step-by-step guide to safely disassembling and reassembling common furniture pieces',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1fb36a5e2-1765128184357.png",
    alt: 'Person using tools to carefully disassemble wooden bed frame',
    duration: '12:30',
    views: 2876,
    category: 'Assembly Guide'
  },
  {
    id: 3,
    title: 'Efficient Box Labeling System',
    description:
    'Color-coded labeling strategy for quick unpacking and room identification',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_153284515-1768148772133.png",
    alt: 'Moving boxes with color-coded labels and clear room designations',
    duration: '6:15',
    views: 1954,
    category: 'Organization'
  },
  {
    id: 4,
    title: 'Moving Day Preparation',
    description:
    'Complete walkthrough of preparing your home and belongings for moving day',
    thumbnail: "https://images.unsplash.com/photo-1645526817591-951a151a4ac1",
    alt: 'Family preparing home with packed boxes and furniture covered in protective wrap',
    duration: '10:20',
    views: 2543,
    category: 'Moving Day'
  }];


  const timelineSteps: TimelineData[] = [
  {
    week: 'Week 8',
    title: 'Initial Planning',
    tasks: [
    'Create moving budget and timeline',
    'Research moving companies and get quotes',
    'Start decluttering and sorting belongings',
    'Begin collecting packing materials']

  },
  {
    week: 'Week 6',
    title: 'Organization Phase',
    tasks: [
    'Book moving company (Almanara Moving)',
    'Create detailed inventory of items',
    'Notify landlord or real estate agent',
    'Start packing non-essential items']

  },
  {
    week: 'Week 4',
    title: 'Utility Management',
    tasks: [
    'Schedule utility disconnections and connections',
    'Update address with banks and institutions',
    'Arrange school transfers if applicable',
    'Pack seasonal items and decorations'],

    isActive: true
  },
  {
    week: 'Week 2',
    title: 'Final Preparations',
    tasks: [
    'Pack remaining items except essentials',
    'Confirm moving day details with Almanara',
    'Prepare essentials box for first week',
    'Clean current residence']

  },
  {
    week: 'Moving Day',
    title: 'Execution',
    tasks: [
    'Final walkthrough of old residence',
    'Supervise loading and inventory check',
    'Secure keys and complete handover',
    'Begin unpacking essentials at new home']

  }];


  const faqs: FAQ[] = [
  {
    id: 1,
    question: 'When should I start planning my move?',
    answer:
    'We recommend starting your moving preparations 8 weeks before your move date. This gives you adequate time to sort belongings, gather packing materials, book services, and handle administrative tasks without stress. For larger homes or international moves, consider starting 10-12 weeks in advance.'
  },
  {
    id: 2,
    question: 'What items should I pack in my essentials box?',
    answer:
    'Your essentials box should include items needed for the first 24-48 hours: toiletries, medications, phone chargers, important documents, basic kitchen items (plates, utensils, coffee maker), bedding, towels, change of clothes, and basic cleaning supplies. Label this box clearly and transport it yourself.'
  },
  {
    id: 3,
    question: 'How do I protect fragile items during the move?',
    answer:
    'Use proper packing materials: bubble wrap for glassware, packing paper for dishes, and foam corners for electronics. Pack fragile items in smaller boxes to prevent overloading. Fill empty spaces with packing peanuts or crumpled paper. Label boxes as "FRAGILE" on all sides and indicate which side should face up.'
  },
  {
    id: 4,
    question: 'What are the best practices for moving during Kuwait\'s summer?',
    answer:
    'Schedule your move early morning (6-10 AM) to avoid peak heat. Ensure climate-sensitive items (electronics, candles, cosmetics) are packed last and unpacked first. Keep water and refreshments available for movers. Consider climate-controlled storage if there\'s a gap between move-out and move-in dates.'
  },
  {
    id: 5,
    question: 'How should I label my moving boxes?',
    answer:
    'Use a color-coding system by room (e.g., blue for kitchen, red for bedroom). Write contents and destination room on at least two sides of each box. Number boxes and maintain a master inventory list. Mark boxes containing fragile items clearly. Include "OPEN FIRST" labels on essential boxes.'
  },
  {
    id: 6,
    question: 'What documents should I keep accessible during the move?',
    answer:
    'Keep a folder with: identification documents (Civil ID, passport), moving contract and insurance papers, property documents (lease agreement, sale contract), utility account information, important contacts list, medical records, and financial documents. Transport this folder personally, never pack it with other items.'
  }];


  const filteredResources =
  activeCategory === 'All' ?
  resources :
  resources.filter((resource) => resource.category === activeCategory);

  const searchedResources = searchQuery ?
  filteredResources.filter(
    (resource) =>
    resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    resource.description.toLowerCase().includes(searchQuery.toLowerCase())
  ) :
  filteredResources;

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="h-20"></div>
        <div className="container mx-auto px-4 py-16">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded w-1/3"></div>
            <div className="h-6 bg-muted rounded w-2/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) =>
              <div key={i} className="h-96 bg-muted rounded-lg"></div>
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary via-secondary to-brand-ocean text-white py-20 lg:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="BookOpenIcon" size={40} variant="solid" className="text-white" />
            </div>
            <h1 className="text-4xl lg:text-6xl font-headline font-bold mb-6">
              Moving Resources & Guides
            </h1>
            <p className="text-xl lg:text-2xl text-white/90 mb-8">
              Expert advice, downloadable guides, and video tutorials to make your move seamless
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Icon
                  name="MagnifyingGlassIcon"
                  size={20}
                  variant="outline"
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-secondary" />

                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search resources, guides, and tips..."
                  className="w-full pl-12 pr-4 py-4 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent" />

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-12 bg-card border-b border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-4xl font-headline font-bold text-primary mb-2">25+</div>
              <div className="text-text-secondary">Downloadable Guides</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-headline font-bold text-primary mb-2">15+</div>
              <div className="text-text-secondary">Video Tutorials</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-headline font-bold text-primary mb-2">10K+</div>
              <div className="text-text-secondary">Downloads</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-headline font-bold text-primary mb-2">24/7</div>
              <div className="text-text-secondary">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-muted">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) =>
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-base ${
              activeCategory === category ?
              'bg-primary text-primary-foreground shadow-soft' :
              'bg-card text-text-primary hover:bg-card-foreground hover:text-card'}`
              }>

                {category}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Downloadable Resources */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-5xl font-headline font-bold text-text-primary mb-4">
              Downloadable Resources
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Free guides, checklists, and templates to help you plan and execute a perfect move
            </p>
          </div>

          {searchedResources.length > 0 ?
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {searchedResources.map((resource) =>
            <ResourceCard key={resource.id} {...resource} />
            )}
            </div> :

          <div className="text-center py-16">
              <Icon
              name="DocumentMagnifyingGlassIcon"
              size={64}
              variant="outline"
              className="text-text-secondary mx-auto mb-4" />

              <p className="text-xl text-text-secondary">
                No resources found matching your search
              </p>
            </div>
          }
        </div>
      </section>

      {/* Video Tutorials */}
      <section className="py-16 lg:py-24 bg-muted">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-5xl font-headline font-bold text-text-primary mb-4">
              Video Tutorials
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Watch our expert team demonstrate professional packing and moving techniques
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {videoTutorials.map((video) =>
            <VideoTutorialCard key={video.id} {...video} />
            )}
          </div>
        </div>
      </section>

      {/* Moving Timeline */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-5xl font-headline font-bold text-text-primary mb-4">
              8-Week Moving Timeline
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Follow our proven timeline to stay organized and stress-free throughout your move
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            {timelineSteps.map((step, index) =>
            <TimelineStep key={index} {...step} />
            )}
          </div>

          <div className="text-center mt-12">
            <button className="inline-flex items-center space-x-2 bg-primary text-primary-foreground px-8 py-4 rounded-lg hover:bg-opacity-90 transition-all duration-base font-semibold shadow-soft">
              <Icon name="ArrowDownTrayIcon" size={20} variant="solid" />
              <span>Download Complete Timeline PDF</span>
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-24 bg-muted">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-5xl font-headline font-bold text-text-primary mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Quick answers to common moving questions from our expert team
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq) =>
            <FAQItem key={faq.id} question={faq.question} answer={faq.answer} />
            )}
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <NewsletterSignup />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-brand-ocean to-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-5xl font-headline font-bold mb-6">
            Ready to Start Your Move?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Get an instant quote or speak with our moving experts available 24/7
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/instant-quote"
              className="inline-flex items-center justify-center space-x-2 bg-accent text-accent-foreground px-8 py-4 rounded-lg hover:bg-opacity-90 transition-all duration-base font-semibold shadow-soft">

              <Icon name="CalculatorIcon" size={24} variant="solid" />
              <span>Get Instant Quote</span>
            </a>
            <a
              href="tel:69911181"
              className="inline-flex items-center justify-center space-x-2 bg-white text-primary px-8 py-4 rounded-lg hover:bg-opacity-90 transition-all duration-base font-semibold shadow-soft">

              <Icon name="PhoneIcon" size={24} variant="solid" />
              <span className="font-accent">Call 6991 1181</span>
            </a>
          </div>
        </div>
      </section>
    </div>);

};

export default ResourcesInteractive;